// Path: src/components/aols/privacy/Privacy.tsx
// Updated to fix the double footer issue by removing the local Footer instance.
// Removed the redundant FullHeightLayout wrapper for better integration with parent layouts.
// Provided complete file content as requested.
// 06/07/25 09:58 AM +07
import React, { useCallback, Fragment } from "react";
import styled from "styled-components";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo from "/logo.jpg";
import { HomeOutlined } from "@ant-design/icons";

const { Title, Paragraph } = Typography;

interface PrivacyProps {}

const BreadcrumbWrapper = styled.div`
  position: absolute;
  top: 10px;
  left: 20px;
  z-index: 1001;

  .ant-breadcrumb a {
    color: #ffcc00;
    transition: color 0.3s ease;
  }

  .ant-breadcrumb a:hover {
    color: #ffaa00;
  }

  @media (max-width: 768px) {
    left: 10px;
    top: 5px;
    .ant-breadcrumb {
      font-size: 12px;
    }
  }
`;

const CustomBreadcrumb = styled.div`
  margin-bottom: 20px;
  text-align: left;
  font-size: 14px;
  line-height: 1.5714285714285714;

  .breadcrumb-item {
    display: inline-block;
  }

  .breadcrumb-link {
    color: #ffaa00 !important;
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: #fce248 !important;
  }

  .breadcrumb-separator {
    color: #fff;
    margin: 0 8px;
  }

  .breadcrumb-non-active {
    color: #fff !important;
  }
`;

const ContentWrapper = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px;
  background: #111;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  color: #fff;

  @media (max-width: 768px) {
    padding: 20px;
    margin: 10px;
  }
`;

const LogoImg = styled.img`
  width: 150px;
  height: auto;
  margin-bottom: 30px;
  cursor: pointer;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(255, 204, 0, 0.3);
  transition: transform 0.3s ease;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    width: 120px;
  }
`;

const MainTitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 20px;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 5px;
  }
`;

interface StyledLinkProps {
  $isButton?: boolean;
}

const MainTitleButton = styled(Link)<StyledLinkProps>`
  background: #ffaa00;
  color: #0427f4;
  font-size: 24px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 6px;
  text-decoration: none;
  transition: background 0.3s ease, transform 0.3s ease;

  &:hover {
    background: #ffcc00;
    color: #0427f4;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    font-size: 18px;
    padding: 6px 12px;
  }
`;

const LinkStyled = styled.a`
  color: #ffaa00;
  text-decoration: none;
  transition: color 0.3s ease;

  &:hover {
    color: #ffcc00;
    text-decoration: underline;
  }

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }
`;

const ContactButton = styled(MainTitleButton)`
  font-size: 18px;
  padding: 6px 12px;
  display: inline-flex;
  align-items: center;
  margin-right: 8px;

  &:focus {
    outline: 2px solid #ffcc00;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    font-size: 16px;
    padding: 4px 10px;
  }
`;

const SectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 30px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const SubsectionTitle = styled(Title)`
  color: #ffffff !important;
  margin-top: 20px;
  margin-bottom: 15px;

  @media (max-width: 768px) {
    font-size: 1.3rem;
  }
`;

const PolicyText = styled(Paragraph)`
  line-height: 1.8;
  color: #ddd;
  margin-bottom: 15px;
  white-space: pre-wrap;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const List = styled.ul`
  margin-left: 20px;
  list-style-type: disc;

  li {
    margin: 10px 0;
    color: #ddd;

    @media (max-width: 768px) {
      font-size: 0.9rem;
    }
  }
`;

const Privacy: React.FC<PrivacyProps> = () => {
  const handleLogoClick = useCallback(() => {
    window.location.href = "https://www.olsme.com";
  }, []);

  return (
    <Fragment>
      <Helmet>
        <title>Privacy Policy - OneLightSystem OLS</title>
        <meta
          name="description"
          content="Review the Privacy Policy for OneLightSystem OLS, covering data protection for OLS student registration, OLSLM assets, OLSME subscriptions, and additional services."
        />
        <meta
          name="keywords"
          content="Privacy Policy, OneLightSystem OLS, OLS Student, OLSLM Assets, OLSME Subscription, Meditation Education, Sun Light Civilization"
        />
        <meta name="author" content="OneLightSystem OLS" />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content="Privacy Policy - OneLightSystem OLS" />
        <meta
          property="og:description"
          content="Learn about OneLightSystem OLS’s Privacy Policy, detailing data collection and usage for meditation education services."
        />
        <meta property="og:url" content="https://www.olsme.com/privacy" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.olsme.com/logo.jpg" />
        <meta property="og:image:alt" content="OneLightSystem OLS Logo" />
        <meta property="og:site_name" content="OneLightSystem OLS" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Privacy Policy - OneLightSystem OLS" />
        <meta
          property="twitter:description"
          content="Explore OneLightSystem OLS’s Privacy Policy for OLS student registration, OLSLM assets, OLSME subscriptions, and more."
        />
        <meta name="twitter:image" content="https://www.olsme.com/logo.jpg" />
        <meta name="twitter:image:alt" content="OneLightSystem OLS Logo" />
        <meta name="twitter:site" content="@onelightsystem" />
        <meta name="twitter:creator" content="@onelightsystem" />
        <link rel="canonical" href="https://www.olsme.com/privacy" />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: "Privacy Policy - OneLightSystem OLS",
            description:
              "Privacy Policy for OneLightSystem OLS, covering services like OLS student registration, OLSLM assets, and OLSME subscriptions.",
            url: "https://www.olsme.com/privacy",
            publisher: {
              "@type": "Organization",
              name: "OneLightSystem OLS",
              logo: "https://www.olsme.com/logo.jpg",
              contactPoint: {
                "@type": "ContactPoint",
                email: "info@olsme.com",
                contactType: "Customer Service",
              },
              address: {
                "@type": "PostalAddress",
                streetAddress: "2033 Ralston Ave",
                addressLocality: "Belmont",
                addressRegion: "CA",
                postalCode: "94002",
                addressCountry: "US",
              },
              founder: {
                "@type": "Person",
                name: "Nazar Pankiv",
                jobTitle: "CEO",
              },
              sameAs: [
                "https://www.olsme.com",
                "https://www.ols-med.net",
                "https://twitter.com/onelightsystem",
                "https://www.facebook.com/onelightsystemols",
                "https://www.linkedin.com/company/one-light-system",
                "https://www.youtube.com/@ONELIGHTSYSTEMOLSMeditation",
              ],
            },
            breadcrumb: {
              "@type": "BreadcrumbList",
              itemListElement: [
                {
                  "@type": "ListItem",
                  position: 1,
                  name: "Home",
                  item: "https://www.olsme.com",
                },
                {
                  "@type": "ListItem",
                  position: 2,
                  name: "Privacy Policy",
                  item: "https://www.olsme.com/privacy",
                },
              ],
            },
          })}
        </script>
      </Helmet>
      <BreadcrumbWrapper>
        <CustomBreadcrumb>
          <span className="breadcrumb-item">
            <Link to="/" className="breadcrumb-link">
              <HomeOutlined /> Home
            </Link>
          </span>
          <span className="breadcrumb-separator">/</span>
          <span className="breadcrumb-item breadcrumb-non-active">
            Privacy Policy
          </span>
        </CustomBreadcrumb>
      </BreadcrumbWrapper>
      <ContentWrapper>
        <LinkStyled href="https://www.olsme.com" aria-label="Go to OneLightSystem OLS homepage">
          <LogoImg src={logo} alt="OneLightSystem OLS Logo" onClick={handleLogoClick} />
        </LinkStyled>
        <MainTitleWrapper>
          <MainTitleButton to="/LightScience" aria-label="Go to LightScience page">
            onelightsystem
          </MainTitleButton>
          <MainTitleButton to="/OLSaeo" aria-label="Go to OLS iee.aeo page">
            OLS iee.aeo
          </MainTitleButton>
          <MainTitleButton to="/OLSsPrivacy" aria-label="Go to OLS Student Privacy page">
            OLS Student Privacy
          </MainTitleButton>
          <MainTitleButton to="/OLSLMprivacy" aria-label="Go to OLSLM Assets Privacy page">
            OLSLM Assets Privacy
          </MainTitleButton>
          <MainTitleButton to="/olsme" aria-label="Go to OLSME page">
            OLSME
          </MainTitleButton>
          <MainTitleButton to="/olsdataprovision" aria-label="Go to OLS Data Provision page">
            OLS Data Provision
          </MainTitleButton>
          <Title level={1} className="main-title-text" style={{ color: "#ffffff" }}>
            Privacy Policy
          </Title>
        </MainTitleWrapper>

        <PolicyText>
          <strong>OneLightSystem OLS Privacy Policy</strong>
          <br />
          Version: 61.3405
          <br />
          Last Updated: April 18, 2025
          <br />
          © OneLightSystem OLS, LLC | OLS iee.aeo | CEO: Nazar Pankiv
        </PolicyText>

        <SectionTitle level={2}>1. Introduction</SectionTitle>
        <PolicyText>
          Welcome to olsme.com, the official portal of OneLightSystem OLS (“OLS”), a Meditation Education Academy offering online registration for OLS student tuition, OLSLM asset management, OLSME subscriptions, and more. By accessing{" "}
          <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
            www.olsme.com
          </LinkStyled>{" "}
          or{" "}
          <LinkStyled href="https://www.ols-med.net" aria-label="Visit ols-med.net">
            www.ols-med.net
          </LinkStyled>, you agree to this Privacy Policy and Terms of Use.
        </PolicyText>

        <SectionTitle level={2}>2. Definitions and Compliance</SectionTitle>
        <PolicyText>
          <strong>OLS</strong>: OneLightSystem OLS, LLC, based in California, USA, owns and operates olsme.com, ols-med.net, and all associated services, trademarks, and copyrights. Unauthorized use of OLS data, trademarks (e.g., “OLS®”), or content is prohibited.
        </PolicyText>
        <SubsectionTitle level={3}>2.1 Founder and CEO: Nazar Pankiv</SubsectionTitle>
        <PolicyText>
          <strong>Ownership</strong>: OLS is dedicated to meditation teachings, spiritual education, scientific research (OLS iense), and counseling (OLS aeo), managing OLSLM and OLSME departments.
        </PolicyText>
        <PolicyText>
          <strong>Copyright</strong>: All content is protected under copyright and trademark laws.
        </PolicyText>

        <SectionTitle level={2}>3. Terms of Service and Non-Disclosure Agreement (NDA)</SectionTitle>
        <PolicyText>
          Using OLS services constitutes a binding NDA.
        </PolicyText>
        <List>
          <li>
            <strong>Scope</strong>: Services include OLS student registration, OLSLM asset management, OLSME subscriptions, and others. Payments are non-refundable.
          </li>
          <li>
            <strong>Confidentiality</strong>: Application data is privileged and confidential. Unauthorized sharing or copying is prohibited.
          </li>
        </List>

        <SectionTitle level={2}>4. Services Covered</SectionTitle>
        <List>
          <li>
            <ContactButton to="/register/olssregister" aria-label="Register as an OLS Student">
              OLS Student
            </ContactButton>
            Enrollment in the Meditation Education Academy.
          </li>
          <li>
            <ContactButton to="/register/olslmregister" aria-label="Register for OLSLM Asset Management">
              OLSLM Assets Management
            </ContactButton>
            Real asset value management and portfolio services.
          </li>
          <li>
            <ContactButton to="/register/olsmeregister" aria-label="Register for OLSME Subscription">
              OLSME Subscription
            </ContactButton>
            Public, corporate, and governmental training packages.
          </li>
          <li>
            <strong>Other Services</strong>: 
            <ul style={{ marginLeft: 20, listStyleType: "circle" }}>
              <li>
                <em>Ticket to Heaven</em>: For individuals over 72 to prepare their light body (
                <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about Ticket to Heaven">
                  inquire
                </LinkStyled>).
              </li>
              <li>
                <em>Location Boost</em>: Request OLS meditation sessions at your location (
                <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about Location Boost">
                  inquire
                </LinkStyled>).
              </li>
              {/* UPDATED: This section now accurately reflects the new referral program */}
              <li>
                <ContactButton to="/referral" aria-label="Learn about the OLS Referral Program">
                  OLS Referral Program
                </ContactButton>
                Explore our Referral Program with a guaranteed 5-level commission structure. Introduce this necessary self-development tool and earn commissions for each referral transaction.{" "}
              </li>
            </ul>
          </li>
        </List>

        <SectionTitle level={2}>5. Verification Process</SectionTitle>
        <PolicyText>
          To become an OLS student, OLSME user, OLSLM affiliate, or other user, you must pass a verification process for online credentials. Credentials are publicly searchable in the OLS database, except for student prospects, verified during the OLS Intro session.
        </PolicyText>

        <SectionTitle level={2}>6. Data Collection and Privacy</SectionTitle>
        <PolicyText>
          <ContactButton to="/olsdataprovision" aria-label="Go to OLS Data Provision page">
            Learn More
          </ContactButton>
          We collect information when you interact with our services, including:
        </PolicyText>
        <List>
          <li>
            <strong>Data Protection</strong>: Application data (e.g., social links, phone numbers) is protected and not shared with third parties. Phone numbers are used solely for verification and remain confidential.
          </li>
          <li>
            <strong>Public Data</strong>: User names may be searchable in the OLS database unless you opt for initials or usernames. OLS uses a centralized, transparent blockchain, accessible only via{" "}
            <LinkStyled href="https://www.ols-med.net" aria-label="Visit ols-med.net">
              www.ols-med.net
            </LinkStyled>{" "}
            (administration) and{" "}
            <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
              www.olsme.com
            </LinkStyled>.
          </li>
        </List>

        <SectionTitle level={2}>7. Evaluation and Access</SectionTitle>
        <List>
          <li>
            <strong>OLSLM Marketplace</strong>: For asset trading and management.
          </li>
          <li>
            <strong>OLS Promoter</strong>: Referral program with manual 5% commission processing (
            <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about OLS Promoter">
              inquire
            </LinkStyled>).
          </li>
          <li>
            <strong>OLSME Subscriptions</strong>: Fees follow the “Light Year Payment Graph.” Corporate/governmental training may incur additional fees. User data remains confidential.
          </li>
        </List>

        <SectionTitle level={2}>8. Personal Illumination Account</SectionTitle>
        <PolicyText>
          <MainTitleButton to="/OLSsPrivacy" aria-label="Go to OLS Student Privacy page">
            OLS Student Privacy
          </MainTitleButton>
          OLS students can create a Personal Illumination Account to mine “Inner Equity Formation (IEF)” through meditation, earning OLSLM “Light Minutes” displayed publicly in profiles. Students access OLSM meditation and olsme, managed by the OLS data center.
        </PolicyText>

        <SectionTitle level={2}>9. OLSLM Assets</SectionTitle>
        <PolicyText>
          <MainTitleButton to="/OLSLMprivacy" aria-label="Go to OLSLM Assets Privacy page">
            Privacy Conditions
          </MainTitleButton>
          OLSLM Light Minutes are real assets for trading, mining, and development via OLS services.
        </PolicyText>
        <List>
          <li>Users must complete verification for credentials.</li>
          <li>OLS iee.aeo manages the OLSLM Marketplace.</li>
        </List>

        <SectionTitle level={2}>10. Additional Services</SectionTitle>
        <List>
          <li>
            <strong>Pulling Light to Your Location</strong>: Request OLS meditation sessions (
            <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about Location Boost">
              inquire
            </LinkStyled>).
          </li>
          <li>
            <strong>Ticket to Heaven</strong>: For individuals over 72 (
            <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about Ticket to Heaven">
              inquire
            </LinkStyled>).
          </li>
          <li>
            <strong>OLS Amba</strong>: Light ambassadoring at international events (
            <LinkStyled href="mailto:info@olsme.com" aria-label="Inquire about OLS Amba">
              info@olsme.com
            </LinkStyled>).
          </li>
        </List>

        <SectionTitle level={2}>11. Intellectual Property</SectionTitle>
        <PolicyText>
          Innovations  <ContactButton to="/OLScalendarTime" aria-label="Go to OLS Calendar Time page">
             Light Calendar and Light Time
          </ContactButton> are proprietary to OLS.{" "}
          
          For <ContactButton to="/license" aria-label="Go to Licesne page">
          Licensing
          </ContactButton> {" "}
          <ContactButton to="/Contact" aria-label="Go to Contact page">
            Contact Us
          </ContactButton> 
        </PolicyText>

        <SectionTitle level={2}>12. Using Our Services</SectionTitle>
        <PolicyText>
          This website provides educational content, not medical advice. Consult healthcare providers for medical concerns. OLS is not liable for misuse.
        </PolicyText>

        <SectionTitle level={2}>13. Fees and Transactions</SectionTitle>
        <List>
          <li>
            <strong>OLSLM Transactions</strong>: 5% transfer fee.
          </li>
          <li>
            <strong>Referrals</strong>: 5% commission.
          </li>
          <li>
            <strong>Maintenance</strong>: Quarterly OLS System Data Maintenance Reports.
          </li>
        </List>

        <SectionTitle level={2}>14. OLS Values</SectionTitle>
        <List>
          <li>
            <strong>Transparency</strong>: Clear communication and data management.
          </li>
          <li>
            <strong>Responsibility</strong>: Commitment to privacy and educational excellence.
          </li>
          <li>
            <strong>Centralized Education</strong>: OLS database ensures privacy and ownership rights.
          </li>
        </List>

        <SectionTitle level={2}>15. Contact and Legal Notices</SectionTitle>
        <PolicyText>
          Websites:{" "}
          <LinkStyled href="https://www.olsme.com" aria-label="Visit olsme.com">
            www.olsme.com
          </LinkStyled>,{" "}
          <LinkStyled href="https://www.ols-med.net" aria-label="Visit ols-med.net">
            www.ols-med.net
          </LinkStyled> (public viewing only).
          <br />
          Ownership: OneLightSystem OLS, LLC, California, USA.
          <br />
          Disclaimer: This policy may be updated; users will be notified.
          <br />
        </PolicyText>
      </ContentWrapper>
    </Fragment>
  );
};

export default Privacy;